// Config
import Config from "./Config";

import {
	ColumnType,
	FieldFilter,
	FieldValue,
	GridColumn,
	ISummaryColumnMetadata,
	SelectedFilter,
	SummaryFunction,
} from "../../schemas/report";

import { useRbacCompanySettingDependentPermission } from "../../../../api/hooks/defs/rbac";

import { defaultReportPageSize, maxReportPageSize } from "../../config";

export const selectQuestionTypeDisplayName = (questionType: string) => {
	const questionTypeDisplayNameMap = {
		calculator: "Calculator",
		date: "Date",
		"goal-creation": "Goal Creation",
		"goal-discussion": "Goal Discussion",
		instructions: "Instructions",
		multiline: "Multiline",
		"multiple-choice": "Multiple Choice",
		"multiple-select": "Multiple Select",
		numeric: "Numeric",
		"rating-scale": "Rating Scale",
		stars: "Stars",
		text: "Text",
	};

	return questionTypeDisplayNameMap[questionType] ? questionTypeDisplayNameMap[questionType] : "Unknown Type";
};

export const selectNonEmptyFilters = (appliedFilters: SelectedFilter[]) => {
	// Filter types where empty array is a valid explicit value (e.g., "all unchecked")
	const preserveEmptyValueTypes = ["surveyStatus"];

	const nonEmptyQuickFilters = appliedFilters.filter((item) => {
		// Preserve checkbox filters even with empty arrays
		if (preserveEmptyValueTypes.includes(item?.type)) {
			return true;
		}
		if (item?.value?.length === 0) {
			return false;
		}
		return true;
	});
	return nonEmptyQuickFilters;
};

// returns the number of filters applied. This is the number displayed on the filter badge
export const getQuickFilterCount = (filters: SelectedFilter[]): number => {
	// Count filters that have non-empty values
	// Note: selectNonEmptyFilters preserves some filters with empty values for state,
	// but we don't want to count those in the badge
	return filters.filter((item) => {
		if (Array.isArray(item?.value) && item.value.length === 0) {
			return false;
		}
		return item?.value != null;
	}).length;
};

export const clampPageSize = (pageSize: number | null): number => {
	if (pageSize) {
		return Math.max(1, Math.min(pageSize, maxReportPageSize));
	}

	return defaultReportPageSize;
};

export const formatSummarizeByAndAggregationOperations = (
	enabledSummarizeFields,
	summaryFunctions: { [key: string]: ISummaryColumnMetadata }
) => {
	const summarizeBy = enabledSummarizeFields.map((field) => {
		const fieldGroup = field.value.split(/_(.*)/s)[0];
		const fieldName = field.value.split(/_(.*)/s)[1];
		return {
			type: Config.fieldGroupLookup[fieldGroup],
			name: fieldName,
			selection: field.selection,
			fieldType: Config.summarizeFields[fieldGroup].find((f) => f.value === field.value)?.type,
		};
	});

	const aggregationOperations = Object.keys(summaryFunctions).map((column) => {
		const fieldGroup = column.split(/_(.*)/s)[0];
		const fieldName = column.split(/_(.*)/s)[1];

		return {
			type: Config.fieldGroupLookup[fieldGroup],
			name: fieldName,
			operation: summaryFunctions[column].currentlySelectedSummaryFunction,
		};
	});

	return { summarizeBy, aggregationOperations };
};

/**
 * Determines if a given column is visible in the grid.
 *
 * Handles the special cases for showing certain columns in the grid based on
 * the summarize by fields.
 *
 * The general requirement for column visibility is:
 * - never show a "summarize By" column in the grid
 * - if showAllAggregationColumns is true, show all columns
 * - if showAllAggregationColumns is false, show only numeric type columns
 * ---- unless the the column meets the special requirement: "has a 1:1
 *      relationship" with a summarizeBy field.
 * ---- These special cases have been hardcoded into the logic below as they
 * are not easily derived from the schema.
 */
export const calculateIsColumnVisible = (
	isSummarizeRowsView: boolean,
	column: string,
	showAllAggregationColumns: boolean,
	enabledSummarizeFields: {
		value: string;
		label: string;
	}[],
	columnInfo: GridColumn,
	columnGroup: string
) => {
	if (!isSummarizeRowsView) {
		return true;
	}

	const duplicatedBySummarizedColumns = enabledSummarizeFields.some((field) => {
		return field.value === column;
	});

	// never show a "summarize By" column in the grid
	if (duplicatedBySummarizedColumns) {
		return false;
	}

	if (showAllAggregationColumns) {
		// unless duplicated, show all aggregation columns
		return true;
	}

	if (!showAllAggregationColumns) {
		if (Config.numericTypes.includes(columnInfo?.fieldValueType || "")) {
			// show numeric type columns (unless duplicated)
			return true;
		}
		// show multiple-choice and multiple-select columns
		if (["multiple-select", "multiple-choice"].includes(columnInfo?.fieldValueType || "")) {
			return true;
		}
		// Special "1 to 1" relationship cases
		if (
			enabledSummarizeFields.some((field) => {
				return field.value === "review_subject";
			}) &&
			columnGroup === "employee"
		) {
			// show employee columns if summarizing by review_subject
			return true;
		}

		if (
			enabledSummarizeFields.some((field) => {
				return field.value === "reviewcycle_name";
			}) &&
			columnGroup === "reviewcycle"
		) {
			// show cycle columns if summarizing by reviewcycle_name
			return true;
		}

		// Show grouped questions column when they're non text
		if (columnInfo?.fieldType === "GROUPED_QUESTION") {
			return columnInfo.fieldValueType !== "text";
		}
	}

	return false;
};

/**
 * Returns an array of available summary functions for a given column.
 *
 * No summary function are allowed if summarizeRowsView is false.
 * All summary functions are allowed if the column is numeric.
 *
 * Only COUNT is allowed if the column is not numeric.
 * ---- unless the the column meets the special requirement: "has a 1:1
 *       relationship" with a summarizeBy field.
 * ---- These special cases have been hardcoded into the logic below as they
 *      are not easily derived from the schema.
 */
export const calculateAvailableSummaryFunctions = (
	isSummarizeRowsView: boolean,
	enabledSummarizeFields: {
		value: string;
		label: string;
	}[],
	columnInfo: GridColumn,
	columnGroup: string
) => {
	if (!isSummarizeRowsView) {
		return [];
	}

	if (Config.numericTypes.includes(columnInfo?.fieldValueType || "")) {
		// numeric column types always have all summary functions
		return [
			SummaryFunction.Maximum,
			SummaryFunction.Minimum,
			SummaryFunction.Average,
			SummaryFunction.Sum,
			SummaryFunction.Count,
		];
	}

	if (
		enabledSummarizeFields.some((field) => {
			return field.value === "review_subject";
		}) &&
		columnGroup === "employee"
	) {
		// special case for employee columns, only Maximum is allowed
		return [SummaryFunction.Maximum];
	}

	if (
		enabledSummarizeFields.some((field) => {
			return field.value === "reviewcycle_name";
		}) &&
		columnGroup === "reviewcycle"
	) {
		// special case for cycle columns, only Maximum is allowed
		return [SummaryFunction.Maximum];
	}

	if (["multiple-select", "multiple-choice"].includes(columnInfo?.fieldValueType || "")) {
		return [SummaryFunction.Concatenate];
	}

	// all others only have count as a summary function option
	return [SummaryFunction.Count];
};

/**
 * Given the store state, returns the currently selected summary function for a
 * given column.
 *
 * Enforces certain special cases where we only allow a single summary function
 * for a given column.
 */
export const selectCurrentSummaryFunction = (
	isSummarizeRowsView: boolean,
	column: string,
	summaryFunctions: {
		[key: string]: SummaryFunction;
	},
	enabledSummarizeFields: {
		value: string;
		label: string;
	}[],
	columnInfo: GridColumn,
	columnGroup: string
) => {
	if (!isSummarizeRowsView) {
		return;
	}

	// numeric types
	if (Config.numericTypes.includes(columnInfo?.fieldValueType || "")) {
		return summaryFunctions[column] ?? SummaryFunction.Average;
	}

	if (
		enabledSummarizeFields.some((field) => {
			return field.value === "review_subject";
		}) &&
		columnGroup === "employee"
	) {
		// special case for employee columns, only Maximum is allowed
		return SummaryFunction.Maximum;
	}

	if (
		enabledSummarizeFields.some((field) => {
			return field.value === "reviewcycle_name";
		}) &&
		columnGroup === "reviewcycle"
	) {
		// special case for cycle columns, only Maximum is allowed
		return SummaryFunction.Maximum;
	}

	if (["multiple-select", "multiple-choice"].includes(columnInfo?.fieldValueType || "")) {
		return SummaryFunction.Concatenate;
	}

	return SummaryFunction.Count;
};

export const getSummarizeBySelectOptions = (columnType) => {
	if (columnType === "date") {
		return Config.dateSummarizeByOptions;
	}
	if (Config.binnableTypes.includes(columnType)) {
		return Config.binSummarizeByOptions;
	}
	return [];
};

export const getSortFieldPath = (gridColumn: GridColumn) => {
	if (
		gridColumn?.fieldType === "GROUPED_QUESTION" &&
		["multiple-select", "multiple-choice"].includes(gridColumn?.fieldValueType || "")
	) {
		// replace last item in fieldPath array with "distinctCount" to sort by the count of distinct answers
		const fieldPath = gridColumn.fieldPath.slice();
		fieldPath[fieldPath.length - 1] = "distinctCount";
		return fieldPath;
	} else if (gridColumn?.fieldValueType === "multiple-select") {
		// replace last item in fieldPath array with "count" to sort by the count of answers
		const fieldPath = gridColumn.fieldPath.slice();
		fieldPath[fieldPath.length - 1] = "count";
		return fieldPath;
	} else if (
		["EMPLOYEE_FIELD", "REVIEW_FORM_FIELD"].includes(gridColumn?.fieldType) &&
		gridColumn?.fieldValueType === "people"
	) {
		// add name to the field path, to sort on the name of the person
		const fieldPath = gridColumn.fieldPath.slice();
		fieldPath.push("name");
		return fieldPath;
	}
	return gridColumn?.fieldPath;
};

export const getSummarizedViewSortFieldPath = (gridColumn: GridColumn) => {
	// grouped questions have a special case where they should be sorted by _id instead of name
	if (gridColumn?.fieldType === "GROUPED_QUESTION" && gridColumn?.fieldPath[0] === "summarizeBy") {
		// replace last item in fieldPath array  "name" with "_id"
		const fieldPath = gridColumn.fieldPath.slice();
		fieldPath[fieldPath.length - 1] = "_id";
		return fieldPath;
	} else if (["multiple-select", "multiple-choice"].includes(gridColumn?.fieldValueType || "")) {
		// replace last item in fieldPath array with "distinctCount" to sort by the count of distinct answers
		const fieldPath = gridColumn.fieldPath.slice();
		fieldPath[fieldPath.length - 1] = "distinctCount";
		return fieldPath;
	}
	return gridColumn?.fieldPath;
};

export const getColumnKeyFromColumnTypeValue = (value) => {
	for (const [key, val] of Object.entries(ColumnType)) {
		if (val === value) {
			return key;
		}
	}
	return undefined;
};

export const getShouldDisplayReviewsInProgressMessage = (
	permissionToCreateReviewReport: boolean,
	appliedFilters: FieldFilter[][],
	reportType: string = ""
) => {
	if (!reportType || reportType !== "reviewReport") {
		return false;
	}
	const statusFilters = appliedFilters.filter((subArray) => subArray.some((filter) => filter.field === "status"));

	const inProgressValues: FieldValue[] = [
		"in-progress-awaiting-authors",
		"in-progress-awaiting-signoff",
		"Active",
		"Scheduled",
	];

	const displayReportIsActiveBanner =
		!permissionToCreateReviewReport &&
		(statusFilters.some((subArray) =>
			subArray.some((filter) => filter?.value && inProgressValues.includes(filter.value))
		) ||
			statusFilters.length === 0);
	return displayReportIsActiveBanner;
};

export const getFieldTypeFromSummnarizeByFieldValue = (fieldValue?: string) => {
	if (!fieldValue) return null;
	const fieldGroup = fieldValue.split(/_(.*)/s)[0];
	return Config.summarizeFields[fieldGroup]?.find((f) => f.value === fieldValue)?.type;
};

export const getLabelFromSummarizeByFieldValue = (fieldValue?: string) => {
	if (!fieldValue) return null;
	const fieldGroup = fieldValue.split(/_(.*)/s)[0];
	return Config.summarizeFields[fieldGroup]?.find((f) => f.value === fieldValue)?.label;
};

const createPermissionCheck = (permission: string) => (permissions: string[]) =>
	permissions?.includes(permission) ?? false;

export const canCreateNewReviewReport = createPermissionCheck("report_analytics:createReport");
export const canViewReviewReportSharedWithManagers = createPermissionCheck(
	"report_analytics:viewReportsListSharedWithManagers"
);
export const canViewReviewReportSharedWithContributors = createPermissionCheck(
	"report_analytics:viewReportsListSharedWithContributors"
);
export const canCreateNewEmployeeReport = createPermissionCheck("report_analytics:createEmployeeReport");
export const canViewEmployeeReport = createPermissionCheck("report_analytics:viewEmployeeReport");
export const canCreateNewGoalReport = createPermissionCheck("report_analytics:createGoalReport");
export const canViewEmployeeReportSharedWithManagers = createPermissionCheck(
	"report_analytics:viewEmployeeReportsListSharedWithManagers"
);
export const canViewEmployeeReportSharedWithContributors = createPermissionCheck(
	"report_analytics:viewEmployeeReportsListSharedWithContributors"
);
export const canViewGoalReportSharedWithManagers = createPermissionCheck(
	"report_analytics:viewGoalReportsListSharedWithManagers"
);
export const canViewGoalReportSharedWithContributors = createPermissionCheck(
	"report_analytics:viewGoalReportsListSharedWithContributors"
);
export const canCreateNewSurveyReport = createPermissionCheck("report_analytics:createSurveyReport");

export const canViewSurveyReportSharedWithManagers = createPermissionCheck(
	"report_analytics:viewSurveyReportsListSharedWithManagers"
);

export const canViewSurveyReportSharedWithContributors = createPermissionCheck(
	"report_analytics:viewSurveyReportsListSharedWithContributors"
);
/* 
The goal of this function is to use user permissions and company settings to 
determine the appropriate sharedWith array values if a user can query 
for review reports but is not an admin.
*/

export const getSharedWithReportRoleValues = (
	permissions: string[] = [],
	managersViewReportsSettingEnabled: boolean = false,
	contributorsViewReportSettingEnabled: boolean = false
) => {
	const roleConfig = [
		{
			role: "managers",
			settingEnabled: managersViewReportsSettingEnabled,
			checks: [
				canViewReviewReportSharedWithManagers,
				canViewGoalReportSharedWithManagers,
				canViewEmployeeReportSharedWithManagers,
				canViewSurveyReportSharedWithManagers,
			],
		},
		{
			role: "contributors",
			settingEnabled: contributorsViewReportSettingEnabled,
			checks: [
				canViewReviewReportSharedWithContributors,
				canViewGoalReportSharedWithContributors,
				canViewEmployeeReportSharedWithContributors,
				canViewSurveyReportSharedWithContributors,
			],
		},
	];

	return roleConfig
		.filter(({ settingEnabled, checks }) => settingEnabled && checks.some((check) => check(permissions)))
		.map(({ role }) => role);
};

type ReviewTypePermissions = {
	permissionToCreateReviewReport: boolean;
	permissionToCreateGoalReport: boolean;
	permissionToViewReviewReportSharedWithManagers: boolean;
	permissionToCreateEmployeeReport: boolean;
	adminPermissions: boolean;
	permissionToViewReviewReportSharedWithContributors: boolean;
	permissionToViewGoalReportSharedWithManagers: boolean;
	permissionToViewGoalReportSharedWithContributors: boolean;
	permissionToCreateSurveyReport: boolean;
	permissionToViewSurveyReportSharedWithManagers: boolean;
	permissionToViewSurveyReportSharedWithContributors: boolean;
};

/* 
   The purpose of this function is get all necessary permission variables based 
   on user permissions and report types for the Reprot & Analytics homepage
*/
export const getReportTypeSpecificPermissions = (permissions: string[] = []): ReviewTypePermissions => {
	const permissionChecks = {
		permissionToCreateReviewReport: canCreateNewReviewReport,
		permissionToCreateGoalReport: canCreateNewGoalReport,
		permissionToViewReviewReportSharedWithManagers: canViewReviewReportSharedWithManagers,
		permissionToCreateEmployeeReport: canCreateNewEmployeeReport,
		permissionToViewReviewReportSharedWithContributors: canViewReviewReportSharedWithContributors,
		permissionToViewGoalReportSharedWithManagers: canViewGoalReportSharedWithManagers,
		permissionToViewGoalReportSharedWithContributors: canViewGoalReportSharedWithContributors,
		permissionToCreateSurveyReport: canCreateNewSurveyReport,
		permissionToViewSurveyReportSharedWithManagers: canViewSurveyReportSharedWithManagers,
		permissionToViewSurveyReportSharedWithContributors: canViewSurveyReportSharedWithContributors,
	};

	const result = Object?.fromEntries(
		Object?.entries(permissionChecks)?.map(([key, check]) => [key, check(permissions)])
	) as Omit<ReviewTypePermissions, "adminPermissions">;

	return {
		...result,
		adminPermissions:
			result?.permissionToCreateReviewReport ||
			result?.permissionToCreateEmployeeReport ||
			result?.permissionToCreateGoalReport ||
			result?.permissionToCreateSurveyReport,
	};
};

export const getUserSpecificCompanySettingDependentPermissions = () => {
	// Group related permissions together
	const managerPermissions = {
		canViewReviewReports: useRbacCompanySettingDependentPermission(
			"managers-can-view-report-analytics",
			"report_analytics",
			"report_analytics:viewReportsListSharedWithManagers",
			false,
			"ff-is-manager-and-contributor-reporting-access-enabled"
		).data,
		canViewGoalReports: useRbacCompanySettingDependentPermission(
			"managers-can-view-report-analytics",
			"report_analytics",
			"report_analytics:viewGoalReportsListSharedWithManagers",
			false,
			"ff-is-manager-and-contributor-reporting-access-enabled"
		).data,
		canViewEmployeeReports: useRbacCompanySettingDependentPermission(
			"managers-can-view-report-analytics",
			"report_analytics",
			"report_analytics:viewEmployeeReportsListSharedWithManagers",
			false,
			"ff-is-manager-and-contributor-reporting-access-enabled"
		).data,
		canViewSurveyReports: useRbacCompanySettingDependentPermission(
			"managers-can-view-report-analytics",
			"report_analytics",
			"report_analytics:viewSurveyReportsListSharedWithManagers",
			false,
			"ff-is-manager-and-contributor-reporting-access-enabled"
		).data,
	};

	const contributorPermissions = {
		canViewReviewReports: useRbacCompanySettingDependentPermission(
			"contributors-can-view-report-analytics",
			"report_analytics",
			"report_analytics:viewReportsListSharedWithContributors",
			false,
			"ff-is-manager-and-contributor-reporting-access-enabled"
		).data,
		canViewGoalReports: useRbacCompanySettingDependentPermission(
			"contributors-can-view-report-analytics",
			"report_analytics",
			"report_analytics:viewGoalReportsListSharedWithContributors",
			false,
			"ff-is-manager-and-contributor-reporting-access-enabled"
		).data,
		canViewEmployeeReports: useRbacCompanySettingDependentPermission(
			"contributors-can-view-report-analytics",
			"report_analytics",
			"report_analytics:viewEmployeeReportsListSharedWithContributors",
			false,
			"ff-is-manager-and-contributor-reporting-access-enabled"
		).data,
		canViewSurveyReports: useRbacCompanySettingDependentPermission(
			"contributors-can-view-report-analytics",
			"report_analytics",
			"report_analytics:viewSurveyReportsListSharedWithContributors",
			false,
			"ff-is-manager-and-contributor-reporting-access-enabled"
		).data,
	};
	return { managerPermissions, contributorPermissions };
};
